import React, { useState, useEffect, useMemo, useLayoutEffect, useRef, useCallback } from 'react';
import { Plane, Check, Users, ChevronDown, ChevronUp, Eye, Search, Filter, X, FileText } from 'lucide-react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FiArrowUpRight } from 'react-icons/fi';
import MainLayout from '../components/Layout/MainLayout';
import toast from 'react-hot-toast';
import clientService from '../services/clientService';
import privateJetService from '../services/privateJetService';
import { PRIVATE_JETS_GALLERY, heroJetsFromGallery } from '../constants/privateJetsGallery';

const HERO_FALLBACK_SRC = heroJetsFromGallery[10]?.src || heroJetsFromGallery[0]?.src;

const initialFormState = {
    clientInfo: {
        clientId: '',
        clientName: '', // For existing clients mostly
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
    },
    firstLeg: {
        dateOfDeparture: '',
        departingAirportArea: '',
        arrivalAirportArea: ''
    },
    returnLeg: {
        dateOfDeparture: '',
        departingAirportArea: '',
        arrivalAirportArea: ''
    },
    additionalInformation: {
        additionalLegs: '',
        preferredDepartureTimes: '',
        numberOfPassengers: 1,
        infantAges: '',
        tripType: 'Round Trip',
        preferredAircraft: '',
        specialRequests: '',
        foodAllergies: '',
        budget: '',
        flownPrivatePreviously: '',
        estimatedLuggage: '',
        oversizedLuggage: '',
        notes: ''
    }
};

const PrivateJetPage = () => {
    const [clients, setClients] = useState([]);
    const [showNewClientForm, setShowNewClientForm] = useState(false);

    const [formData, setFormData] = useState(initialFormState);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [showAllGallery, setShowAllGallery] = useState(false);
    const [jetCategoryFilter, setJetCategoryFilter] = useState('All');
    const [jetSearch, setJetSearch] = useState('');
    const [selectedJet, setSelectedJet] = useState(null);
    const [selectedJetImageIndex, setSelectedJetImageIndex] = useState(0);

    const [heroSlideIndex, setHeroSlideIndex] = useState(0);
    const [heroMediaOpacity, setHeroMediaOpacity] = useState(1);
    const heroFirstPaint = useRef(true);

    const heroSlides = useMemo(() => heroJetsFromGallery.filter(Boolean), []);

    const filteredJets = useMemo(() => {
        const q = jetSearch.trim().toLowerCase();
        return PRIVATE_JETS_GALLERY.filter((jet) => {
            const catOk = jetCategoryFilter === 'All' || jet.category === jetCategoryFilter;
            const searchOk =
                !q ||
                jet.name.toLowerCase().includes(q) ||
                jet.category.toLowerCase().includes(q);
            return catOk && searchOk;
        });
    }, [jetCategoryFilter, jetSearch]);

    useEffect(() => {
        setSelectedJetImageIndex(0);
    }, [selectedJet]);
    const heroCount = heroSlides.length;
    const activeHeroIndex = heroCount ? heroSlideIndex % heroCount : 0;
    const currentHeroJet = heroCount ? heroSlides[activeHeroIndex] : null;
    const nextHeroJet = heroCount > 1 ? heroSlides[(activeHeroIndex + 1) % heroCount] : null;
    const heroVisualKey = currentHeroJet?.name ?? 'empty';

    useEffect(() => {
        if (heroCount < 2) return;
        const t = setInterval(() => {
            setHeroSlideIndex((i) => (i + 1) % heroCount);
        }, 18000);
        return () => clearInterval(t);
    }, [heroCount]);

    useLayoutEffect(() => {
        if (heroFirstPaint.current) {
            heroFirstPaint.current = false;
            return;
        }
        setHeroMediaOpacity(0);
        const t = window.setTimeout(() => setHeroMediaOpacity(1), 100);
        return () => window.clearTimeout(t);
    }, [heroVisualKey]);

    const goHeroPrev = () => {
        if (!heroCount) return;
        setHeroSlideIndex((i) => (i - 1 + heroCount) % heroCount);
    };

    const goHeroNext = () => {
        if (!heroCount) return;
        setHeroSlideIndex((i) => (i + 1) % heroCount);
    };

    const scrollToInquiryForm = useCallback(() => {
        document.getElementById('private-jet-inquiry-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    // Inquiries States
    const [showInquiries, setShowInquiries] = useState(false);
    const [inquiries, setInquiries] = useState([]);
    const [inquiriesLoading, setInquiriesLoading] = useState(false);
    const [inqPage, setInqPage] = useState(1);
    const [inqTotalPages, setInqTotalPages] = useState(1);
    const [inqSearch, setInqSearch] = useState('');
    const [inqStatus, setInqStatus] = useState('');
    const [selectedInquiry, setSelectedInquiry] = useState(null);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const responseData = await clientService.getAgentClients();
                if (responseData && responseData.data) {
                    setClients(responseData.data);
                } else if (Array.isArray(responseData)) {
                    setClients(responseData);
                }
            } catch (error) {
                console.error("Failed to load clients", error);
            }
        };
        fetchClients();
    }, []);

    const loadInquiries = async () => {
        setInquiriesLoading(true);
        try {
            const result = await privateJetService.getInquiries(inqPage, 10, inqSearch, inqStatus);
            setInquiries(result.requests || result.data || result || []);
            setInqTotalPages(result.totalPages || 1);
        } catch (error) {
            console.error("Failed to load inquiries", error);
            toast.error("Failed to load previous inquiries.");
        } finally {
            setInquiriesLoading(false);
        }
    };

    useEffect(() => {
        if (showInquiries) {
            loadInquiries();
        }
    }, [showInquiries, inqPage, inqSearch, inqStatus]);

    const handleClientChange = (e) => {
        const selectedId = e.target.value;
        if (selectedId === 'new') {
            setShowNewClientForm(true);
            setFormData({
                ...formData,
                clientInfo: {
                    ...formData.clientInfo,
                    clientId: '',
                    clientName: '',
                    firstName: '',
                    lastName: '',
                    emailAddress: '',
                    phoneNumber: '',
                    address: '',
                    city: '',
                    state: '',
                    postalCode: '',
                    country: ''
                }
            });
        } else {
            setShowNewClientForm(false);
            const client = clients.find(c => c._id === selectedId);
            if (client) {
                setFormData({
                    ...formData,
                    clientInfo: {
                        ...formData.clientInfo,
                        clientId: client._id,
                        clientName: `${client.firstName} ${client.lastName}`.trim() || client.name,
                        emailAddress: client.email || '',
                        phoneNumber: client.phone || client.phoneNumber || ''
                    }
                });
            }
        }
    };

    const handleChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const resetForm = () => {
        setFormData(initialFormState);
        setShowNewClientForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await privateJetService.submitInquiry(formData);

            // Show popup & reset
            setSubmitSuccess(true);
            resetForm();
            toast.success("Private jet request submitted successfully");
        } catch (error) {
            console.error("Failed to submit inquiry", error);
            toast.error(error.response?.data?.error || "Failed to submit request.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClasses = "mt-2 w-full px-4 h-12 text-sm border border-gray-200 bg-white focus:border-gray-900 text-gray-900 font-poppinsLight outline-none disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200";
    const labelClasses = "block text-[10px] tracking-[0.22em] uppercase font-poppinsRegular text-gray-600";
    const textareaClasses = "mt-2 w-full p-4 text-sm border border-gray-200 bg-white focus:border-gray-900 text-gray-900 font-poppinsLight outline-none transition-colors duration-200";

    return (
        <MainLayout>
            <div className="min-h-screen pb-20" style={{ backgroundColor: '#f7f7f7' }}>
                {/* Hero */}
                <section className="relative overflow-hidden bg-white" style={{ minHeight: 'min(62svh, 36rem)' }}>

                    {/* ── Right panel: full-height image ── */}
                    <div className="hidden lg:block absolute top-0 right-0 bottom-0 w-[55%] z-0 overflow-hidden"
                        style={{ clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)' }}>
                        <div
                            className="absolute inset-0 transition-opacity duration-700 ease-out"
                            style={{ opacity: heroMediaOpacity }}
                        >
                            <img
                                src={currentHeroJet?.src || HERO_FALLBACK_SRC}
                                alt=""
                                className="w-full h-full object-cover transition-transform duration-700"
                            />
                        </div>

                        {/* Floating aircraft info card — bottom right */}
                        {currentHeroJet && (
                            <div className="absolute bottom-8 right-7 z-10">
                                <div className="bg-white/90 backdrop-blur-sm border border-white/60 px-5 py-4 text-gray-900 max-w-[220px] overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                                    <p className="text-[9px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-1.5 truncate">Now Showing</p>
                                    <p className="font-AXONALLPrimeRegular uppercase text-sm tracking-wide leading-tight mb-1 text-gray-900 line-clamp-2">{currentHeroJet.name}</p>
                                    <p className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400 truncate">{currentHeroJet.category}</p>
                                </div>
                            </div>
                        )}

                        {/* Slideshow nav — bottom left of image panel */}
                        {heroCount > 1 && (
                            <div className="absolute bottom-8 left-6 z-10 flex items-center gap-1">
                                <button type="button" onClick={goHeroPrev}
                                    className="p-2 bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200"
                                    aria-label="Previous">
                                    <FaChevronLeft size={10} />
                                </button>
                                <button type="button" onClick={goHeroNext}
                                    className="p-2 bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200"
                                    aria-label="Next">
                                    <FaChevronRight size={10} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile background */}
                    <div className="lg:hidden absolute inset-0 z-0">
                        <div className="absolute inset-0 transition-opacity duration-700" style={{ opacity: heroMediaOpacity }}>
                            <img src={currentHeroJet?.src || HERO_FALLBACK_SRC} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0 bg-black/70" />
                    </div>

                    {/* ── Left content panel ── */}
                    <div className="relative z-10 lg:w-[50%] flex flex-col">

                        {/* Top nav strip */}
                        <div className="border-b border-white/20 lg:border-gray-100 px-6 sm:px-8 lg:px-12 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Plane className="w-3.5 h-3.5 text-white/50 lg:text-gray-400" aria-hidden />
                                <span className="text-[9px] tracking-[0.35em] uppercase font-poppinsLight text-white/50 lg:text-gray-400">
                                    Axonall Private Aviation
                                </span>
                            </div>
                            {heroCount > 1 && (
                                <span className="text-[9px] tracking-[0.25em] font-poppinsLight text-white/35 lg:text-gray-300">
                                    {String(activeHeroIndex + 1).padStart(2, '0')} / {String(heroCount).padStart(2, '0')}
                                </span>
                            )}
                        </div>

                        {/* Main text block */}
                        <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 lg:px-12 py-6">

                            {/* Eyebrow */}
                            <div className="flex items-center gap-3 mb-4 overflow-hidden">
                                <div className="w-6 h-px bg-white/30 lg:bg-gray-300 shrink-0" />
                                <span className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-white/50 lg:text-gray-400 min-w-0 truncate">
                                    {currentHeroJet ? currentHeroJet.category : 'Private Aviation · Axonall Reserve'}
                                </span>
                            </div>

                            {/* Headline */}
                            <h1 className="font-AXONALLPrimeRegular uppercase tracking-wide text-white lg:text-gray-900 mb-4 overflow-hidden"
                                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', lineHeight: '1.2' }}>
                                {currentHeroJet ? (
                                    <span className="block line-clamp-3">{currentHeroJet.name}</span>
                                ) : (
                                    <>
                                        <span className="block">Fly Private.</span>
                                        <span className="block">Arrive in Style.</span>
                                    </>
                                )}
                            </h1>

                            {/* Ruled divider + body copy */}
                            <div className="flex gap-4 mb-5">
                                <div className="w-px bg-white/25 lg:bg-gray-200 shrink-0 mt-1" />
                                <p className="text-white/65 lg:text-gray-500 font-poppinsLight text-sm leading-relaxed max-w-xs">
                                    Share your route and client details — our concierge team will source the perfect aircraft and handle every detail from door to door.
                                </p>
                            </div>

                            {/* CTAs */}
                            <div className="flex flex-wrap gap-3 mb-5">
                                <button
                                    type="button"
                                    onClick={scrollToInquiryForm}
                                    className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 bg-white text-gray-900 lg:bg-gray-900 lg:text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:opacity-90 transition-all duration-200"
                                >
                                    Request a Quote
                                    <FiArrowUpRight className="text-sm" strokeWidth={2.5} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('fleet-gallery')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                    className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 border border-white/50 text-white lg:border-gray-300 lg:text-gray-700 text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:border-white lg:hover:border-gray-900 lg:hover:text-gray-900 transition-all duration-200"
                                >
                                    Browse Fleet
                                </button>
                            </div>

                        </div>

                        {/* Bottom strip: progress dots */}
                        <div className="border-t border-white/20 lg:border-gray-100 px-6 sm:px-8 lg:px-12 py-3 flex items-center justify-end gap-6">
                            {heroCount > 1 && (
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {heroSlides.map((_, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => setHeroSlideIndex(idx)}
                                            aria-label={`Aircraft ${idx + 1}`}
                                            className={`transition-all duration-300 ${
                                                idx === activeHeroIndex
                                                    ? 'w-5 h-[2px] bg-white lg:bg-gray-900'
                                                    : 'w-[2px] h-[2px] bg-white/35 lg:bg-gray-300 rounded-full hover:bg-white/60 lg:hover:bg-gray-500'
                                            }`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 mt-20">
                    <div className="flex justify-end mb-6">
                        <button
                            onClick={() => setShowInquiries(!showInquiries)}
                            className="inline-flex items-center space-x-2.5 px-6 py-3 border border-gray-900 bg-gray-900 text-white text-[11px] tracking-[0.2em] uppercase font-poppinsRegular hover:bg-black transition-colors duration-200"
                        >
                            <FileText className="w-4 h-4" />
                            <span>{showInquiries ? 'New Inquiry' : 'View Previous Inquiries'}</span>
                        </button>
                    </div>

                    {showInquiries ? (
                        <div className="bg-white border border-gray-200 p-6 md:p-10" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                                <div>
                                    <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-1">Agent Portal</p>
                                    <h3 className="text-2xl font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900">Previous Inquiries</h3>
                                </div>

                                <div className="flex items-center space-x-3 w-full md:w-auto">
                                    <div className="relative w-full md:w-64">
                                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search client or email..."
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 bg-white text-sm font-poppinsLight text-gray-900 outline-none focus:border-gray-900 transition-colors duration-200"
                                            value={inqSearch}
                                            onChange={(e) => { setInqSearch(e.target.value); setInqPage(1); }}
                                        />
                                    </div>
                                    <div className="relative">
                                        <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        <select
                                            className="pl-10 pr-4 py-2.5 border border-gray-200 bg-white text-sm font-poppinsLight text-gray-900 outline-none focus:border-gray-900 transition-colors duration-200 appearance-none"
                                            value={inqStatus}
                                            onChange={(e) => { setInqStatus(e.target.value); setInqPage(1); }}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Reviewed">Reviewed</option>
                                            <option value="Fulfilled">Fulfilled</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {inquiriesLoading ? (
                                <div className="flex justify-center items-center py-20">
                                    <div className="w-7 h-7 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
                                </div>
                            ) : inquiries.length === 0 ? (
                                <div className="text-center py-16 bg-gray-50 border border-dashed border-gray-200">
                                    <FileText className="w-10 h-10 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 font-poppinsLight text-[11px] tracking-[0.2em] uppercase">No inquiries found.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse min-w-[800px]">
                                            <thead>
                                                <tr className="border-b border-gray-200 text-[10px] text-gray-400 font-poppinsRegular uppercase tracking-[0.18em]">
                                                    <th className="pb-4 px-4 font-normal">Client Name</th>
                                                    <th className="pb-4 px-4 font-normal">Email</th>
                                                    <th className="pb-4 px-4 font-normal">Date (First Leg)</th>
                                                    <th className="pb-4 px-4 font-normal">Route</th>
                                                    <th className="pb-4 px-4 font-normal">Status</th>
                                                    <th className="pb-4 px-4 font-normal text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inquiries.map(inq => (
                                                    <tr key={inq._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150">
                                                        <td className="py-4 px-4 text-gray-900 font-poppinsMedium text-sm">{inq.clientName}</td>
                                                        <td className="py-4 px-4 text-gray-500 font-poppinsLight text-sm">{inq.emailAddress}</td>
                                                        <td className="py-4 px-4 text-gray-500 font-poppinsLight text-sm">
                                                            {new Date(inq.firstLeg?.dateOfDeparture).toLocaleDateString()}
                                                        </td>
                                                        <td className="py-4 px-4 text-gray-500 font-poppinsLight text-sm">
                                                            {inq.firstLeg?.departingAirportArea} <span className="mx-1 text-gray-300">→</span> {inq.firstLeg?.arrivalAirportArea}
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <span className={`px-2.5 py-1 text-[10px] tracking-[0.12em] uppercase font-poppinsRegular ${inq.status === 'Pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                                                    inq.status === 'Reviewed' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                                        inq.status === 'Fulfilled' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                                            'bg-gray-100 text-gray-600 border border-gray-200'
                                                                }`}>
                                                                {inq.status || 'Pending'}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4 text-right">
                                                            <button
                                                                onClick={() => setSelectedInquiry(inq)}
                                                                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors inline-block"
                                                                title="View Details"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {inqTotalPages > 1 && (
                                        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
                                            <p className="text-[11px] text-gray-400 font-poppinsLight tracking-[0.1em]">
                                                Page <span className="font-poppinsMedium text-gray-700">{inqPage}</span> of <span className="font-poppinsMedium text-gray-700">{inqTotalPages}</span>
                                            </p>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => setInqPage(prev => Math.max(1, prev - 1))}
                                                    disabled={inqPage === 1}
                                                    className="px-5 py-2 border border-gray-200 text-[11px] tracking-[0.15em] uppercase text-gray-700 font-poppinsRegular hover:bg-gray-900 hover:text-white hover:border-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    onClick={() => setInqPage(prev => Math.min(inqTotalPages, prev + 1))}
                                                    disabled={inqPage === inqTotalPages}
                                                    className="px-5 py-2 border border-gray-200 text-[11px] tracking-[0.15em] uppercase text-gray-700 font-poppinsRegular hover:bg-gray-900 hover:text-white hover:border-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white border border-gray-200 p-6 md:p-10" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                            <form id="private-jet-inquiry-form" onSubmit={handleSubmit} className="space-y-12">

                                {/* CLIENT INFORMATION */}
                                <section>
                                    <div className="flex items-center space-x-4 mb-6 pb-4 border-b border-gray-100">
                                        <div className="w-9 h-9 border border-gray-200 flex items-center justify-center text-gray-700">
                                            <Users className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsLight text-gray-400">Step 1</p>
                                            <h3 className="text-lg font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900">Client Information</h3>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div className="md:col-span-2">
                                            <label className={labelClasses}>Select Client <span className="text-red-500">*</span></label>
                                            <select
                                                className={inputClasses}
                                                required
                                                onChange={handleClientChange}
                                                value={formData.clientInfo.clientId || (showNewClientForm ? 'new' : '')}
                                            >
                                                <option value="" disabled>Select an existing client</option>
                                                <option value="new" className="font-bold text-blue-600">+ Register New Client</option>
                                                {clients.map(c => (
                                                    <option key={c._id} value={c._id}>
                                                        {c.firstName} {c.lastName} ({c.email})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {showNewClientForm && (
                                        <div className="bg-gray-50 p-6 border border-gray-200 mb-6 mt-4">
                                            <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-500 mb-4">New Client Details</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className={labelClasses}>First Name <span className="text-red-500">*</span></label>
                                                    <input type="text" required className={inputClasses}
                                                        value={formData.clientInfo.firstName} onChange={(e) => {
                                                            handleChange('clientInfo', 'firstName', e.target.value);
                                                            handleChange('clientInfo', 'clientName', `${e.target.value} ${formData.clientInfo.lastName}`);
                                                        }} />
                                                </div>
                                                <div>
                                                    <label className={labelClasses}>Last Name <span className="text-red-500">*</span></label>
                                                    <input type="text" required className={inputClasses}
                                                        value={formData.clientInfo.lastName} onChange={(e) => {
                                                            handleChange('clientInfo', 'lastName', e.target.value);
                                                            handleChange('clientInfo', 'clientName', `${formData.clientInfo.firstName} ${e.target.value}`);
                                                        }} />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className={labelClasses}>Address <span className="text-red-500">*</span></label>
                                                    <input type="text" required className={inputClasses}
                                                        value={formData.clientInfo.address} onChange={(e) => handleChange('clientInfo', 'address', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className={labelClasses}>City <span className="text-red-500">*</span></label>
                                                    <input type="text" required className={inputClasses}
                                                        value={formData.clientInfo.city} onChange={(e) => handleChange('clientInfo', 'city', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className={labelClasses}>State <span className="text-red-500">*</span></label>
                                                    <input type="text" required className={inputClasses}
                                                        value={formData.clientInfo.state} onChange={(e) => handleChange('clientInfo', 'state', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className={labelClasses}>Postal Code <span className="text-red-500">*</span></label>
                                                    <input type="text" required className={inputClasses}
                                                        value={formData.clientInfo.postalCode} onChange={(e) => handleChange('clientInfo', 'postalCode', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className={labelClasses}>Country <span className="text-red-500">*</span></label>
                                                    <input type="text" required className={inputClasses}
                                                        value={formData.clientInfo.country} onChange={(e) => handleChange('clientInfo', 'country', e.target.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className={labelClasses}>Email Address <span className="text-red-500">*</span></label>
                                            <input type="email" required className={inputClasses}
                                                value={formData.clientInfo.emailAddress}
                                                onChange={(e) => handleChange('clientInfo', 'emailAddress', e.target.value)}
                                                disabled={!showNewClientForm && formData.clientInfo.clientId !== ''}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Phone Number <span className="text-red-500">*</span></label>
                                            <input type="tel" required className={inputClasses}
                                                value={formData.clientInfo.phoneNumber}
                                                onChange={(e) => handleChange('clientInfo', 'phoneNumber', e.target.value)}
                                                disabled={!showNewClientForm && formData.clientInfo.clientId !== ''}
                                            />
                                        </div>
                                    </div>
                                </section>

                                {/* FIRST LEG */}
                                <section>
                                    <div className="flex items-center space-x-4 mb-6 pb-4 border-b border-gray-100">
                                        <div className="w-9 h-9 border border-gray-200 flex items-center justify-center text-gray-700">
                                            <Plane className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsLight text-gray-400">Step 2</p>
                                            <h3 className="text-lg font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900">First Leg</h3>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <label className={labelClasses}>Date of Departure <span className="text-red-500">*</span></label>
                                            <input type="date" required className={inputClasses}
                                                value={formData.firstLeg.dateOfDeparture}
                                                onChange={(e) => handleChange('firstLeg', 'dateOfDeparture', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Departing Airport/Area <span className="text-red-500">*</span></label>
                                            <input type="text" required placeholder="Eg: JFK / New York" className={inputClasses}
                                                value={formData.firstLeg.departingAirportArea}
                                                onChange={(e) => handleChange('firstLeg', 'departingAirportArea', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Arrival Airport/Area <span className="text-red-500">*</span></label>
                                            <input type="text" required placeholder="Eg: LAX / Los Angeles" className={inputClasses}
                                                value={formData.firstLeg.arrivalAirportArea}
                                                onChange={(e) => handleChange('firstLeg', 'arrivalAirportArea', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </section>

                                {/* RETURN LEG */}
                                <section>
                                    <div className="flex items-center space-x-4 mb-6 pb-4 border-b border-gray-100">
                                        <div className="w-9 h-9 border border-gray-200 flex items-center justify-center text-gray-700">
                                            <Plane className="w-4 h-4 transform rotate-180" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsLight text-gray-400">Step 3 — Optional</p>
                                            <h3 className="text-lg font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900">Return Leg</h3>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <label className={labelClasses}>Date of Departure</label>
                                            <input type="date" className={inputClasses}
                                                value={formData.returnLeg.dateOfDeparture}
                                                onChange={(e) => handleChange('returnLeg', 'dateOfDeparture', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Departing Airport/Area</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.returnLeg.departingAirportArea}
                                                onChange={(e) => handleChange('returnLeg', 'departingAirportArea', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Arrival Airport/Area</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.returnLeg.arrivalAirportArea}
                                                onChange={(e) => handleChange('returnLeg', 'arrivalAirportArea', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </section>

                                {/* ADDITIONAL INFORMATION */}
                                <section>
                                    <div className="mb-6 pb-4 border-b border-gray-100">
                                        <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsLight text-gray-400 mb-1">Step 4</p>
                                        <h3 className="text-lg font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900">Additional Information</h3>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className={labelClasses}>Trip Type <span className="text-red-500">*</span></label>
                                            <select className={inputClasses}
                                                value={formData.additionalInformation.tripType}
                                                onChange={(e) => handleChange('additionalInformation', 'tripType', e.target.value)}
                                            >
                                                <option value="Round Trip">Round Trip</option>
                                                <option value="One Way">One Way</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Number of Passengers <span className="text-red-500">*</span></label>
                                            <input type="number" min="1" required className={inputClasses}
                                                value={formData.additionalInformation.numberOfPassengers}
                                                onChange={(e) => handleChange('additionalInformation', 'numberOfPassengers', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className={labelClasses}>Preferred Departure Times</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.additionalInformation.preferredDepartureTimes}
                                                onChange={(e) => handleChange('additionalInformation', 'preferredDepartureTimes', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Infant Ages (if any)</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.additionalInformation.infantAges}
                                                onChange={(e) => handleChange('additionalInformation', 'infantAges', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6 mb-6">
                                        <div>
                                            <label className={labelClasses}>Additional Legs (if any)</label>
                                            <textarea rows="2" className={textareaClasses}
                                                value={formData.additionalInformation.additionalLegs}
                                                onChange={(e) => handleChange('additionalInformation', 'additionalLegs', e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className={labelClasses}>Preferred Aircraft / Jet-Class</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.additionalInformation.preferredAircraft}
                                                onChange={(e) => handleChange('additionalInformation', 'preferredAircraft', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Budget for Trip</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.additionalInformation.budget}
                                                onChange={(e) => handleChange('additionalInformation', 'budget', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Estimated Amount of Luggage</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.additionalInformation.estimatedLuggage}
                                                onChange={(e) => handleChange('additionalInformation', 'estimatedLuggage', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Any Oversized Luggage?</label>
                                            <input type="text" placeholder="Eg: Golf clubs, skis" className={inputClasses}
                                                value={formData.additionalInformation.oversizedLuggage}
                                                onChange={(e) => handleChange('additionalInformation', 'oversizedLuggage', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6 mb-6">
                                        <div>
                                            <label className={labelClasses}>Special Requests or Needs</label>
                                            <textarea rows="2" className={textareaClasses}
                                                value={formData.additionalInformation.specialRequests}
                                                onChange={(e) => handleChange('additionalInformation', 'specialRequests', e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className={labelClasses}>Food Allergies?</label>
                                            <textarea rows="2" className={textareaClasses}
                                                value={formData.additionalInformation.foodAllergies}
                                                onChange={(e) => handleChange('additionalInformation', 'foodAllergies', e.target.value)}
                                            ></textarea>
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Has Client Flown Private Previously?</label>
                                            <input type="text" className={inputClasses}
                                                value={formData.additionalInformation.flownPrivatePreviously}
                                                onChange={(e) => handleChange('additionalInformation', 'flownPrivatePreviously', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6 mb-6">
                                        <div>
                                            <label className={labelClasses}>Notes to help us come back with the strongest options</label>
                                            <textarea rows="3" className={textareaClasses}
                                                value={formData.additionalInformation.notes}
                                                onChange={(e) => handleChange('additionalInformation', 'notes', e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>
                                </section>

                                <div className="pt-8 border-t border-gray-100">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full inline-flex justify-center items-center px-6 py-4 bg-gray-900 text-white text-[11px] tracking-[0.3em] uppercase font-poppinsRegular hover:bg-black disabled:opacity-60 transition-colors duration-200"
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* FLEET GALLERY SECTION */}
                    <div id="fleet-gallery" className="mt-20 mb-20">
                        <div className="max-w-6xl mx-auto px-4 mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                            <div>
                                <p className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight text-gray-400 mb-2">Featured Fleet</p>
                                <h2 className="text-3xl font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900">Private Jets Gallery</h2>
                                <div className="w-8 h-px bg-gray-300 mt-3" />
                            </div>
                            <p className="text-sm font-poppinsLight text-gray-500 max-w-xs">Discover our premier collection of ultimate luxury aircraft</p>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4 max-w-6xl mx-auto px-4">
                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <select
                                        className="pl-10 pr-4 py-2.5 border border-gray-200 bg-white text-sm font-poppinsLight text-gray-900 outline-none focus:border-gray-900 transition-colors duration-200 appearance-none"
                                        value={jetCategoryFilter}
                                        onChange={(e) => setJetCategoryFilter(e.target.value)}
                                    >
                                        <option value="All">All Categories</option>
                                        <option value="Light Jet">Light Jet</option>
                                        <option value="Midsize Jet">Midsize Jet</option>
                                        <option value="Heavy Jet">Heavy Jet</option>
                                        <option value="Turboprop">Turboprop</option>
                                    </select>
                                </div>
                            </div>
                            <div className="relative w-full sm:w-64">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Search aircraft..."
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 bg-white text-sm font-poppinsLight text-gray-900 outline-none focus:border-gray-900 transition-colors duration-200"
                                    value={jetSearch}
                                    onChange={(e) => setJetSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        {filteredJets.length === 0 ? (
                            <div className="text-center py-16 max-w-6xl mx-auto px-4 border border-dashed border-gray-200 bg-gray-50">
                                <p className="text-gray-500 font-poppinsLight text-[11px] tracking-[0.2em] uppercase">No aircraft match your filters.</p>
                                <button
                                    type="button"
                                    onClick={() => { setJetCategoryFilter('All'); setJetSearch(''); }}
                                    className="mt-4 text-[11px] tracking-[0.15em] uppercase text-gray-900 underline font-poppinsRegular"
                                >
                                    Clear filters
                                </button>
                            </div>
                        ) : (
                            <>
                                <div
                                    className="grid gap-4 max-w-6xl mx-auto px-4"
                                    style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gridAutoRows: '200px' }}
                                >
                                    {filteredJets.slice(0, showAllGallery ? filteredJets.length : 6).map((jet, index) => (
                                        <div
                                            key={`${jet.name}-${index}`}
                                            className={`relative bg-gray-100 overflow-hidden group cursor-pointer ${jet.size === 'large' ? 'row-span-2' : ''}`}
                                            onClick={() => {
                                                setSelectedJet(jet);
                                                setSelectedJetImageIndex(0);
                                            }}
                                        >
                                            <div className="absolute inset-0">
                                                <img
                                                    src={jet.src}
                                                    alt={jet.name}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                                                <div className="p-5 w-full transform translate-y-3 group-hover:translate-y-0 transition-transform duration-500">
                                                    <h4 className="text-base font-AXONALLPrimeRegular uppercase tracking-wide text-white">{jet.name}</h4>
                                                    <p className="text-white/70 font-poppinsLight text-xs mt-1 tracking-[0.08em]">{jet.category} · {jet.capacity}</p>
                                                    <div className="w-8 h-px bg-white/60 mt-2" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {filteredJets.length > 6 && (
                                    <div className="mt-10 text-center">
                                        <button
                                            type="button"
                                            onClick={() => setShowAllGallery(!showAllGallery)}
                                            className="inline-flex items-center space-x-2.5 px-8 py-3 border border-gray-300 bg-white text-[11px] tracking-[0.2em] uppercase text-gray-700 font-poppinsRegular hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all duration-200"
                                        >
                                            <span>
                                                {showAllGallery
                                                    ? `Show Less`
                                                    : `View All (${filteredJets.length - 6} more)`}
                                            </span>
                                            {showAllGallery ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                </div>

                {selectedJet && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                        <div className="bg-white shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-300 relative">
                            <button
                                type="button"
                                onClick={() => setSelectedJet(null)}
                                className="absolute top-5 right-5 text-white hover:text-gray-200 transition-colors p-2 bg-black/30 hover:bg-black/50 z-10"
                                aria-label="Close aircraft details"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="relative h-96 md:h-[480px]">
                                <img
                                    src={selectedJet.src}
                                    alt={selectedJet.name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-8">
                                    <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-white/60 mb-1">{selectedJet.category}</p>
                                    <h3 className="text-3xl font-AXONALLPrimeRegular uppercase tracking-wide text-white">{selectedJet.name}</h3>
                                    <div className="w-8 h-px bg-white/40 mt-3" />
                                </div>
                            </div>
                            <div className="p-8">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mb-8 border border-gray-200 divide-x divide-gray-200">
                                    <div className="text-center py-5 px-4">
                                        <div className="text-xl font-AXONALLPrimeRegular text-gray-900 mb-1">{selectedJet.capacity}</div>
                                        <div className="text-[10px] text-gray-400 font-poppinsRegular uppercase tracking-[0.2em]">Capacity</div>
                                    </div>
                                    <div className="text-center py-5 px-4">
                                        <div className="text-xl font-AXONALLPrimeRegular text-gray-900 mb-1">{selectedJet.range}</div>
                                        <div className="text-[10px] text-gray-400 font-poppinsRegular uppercase tracking-[0.2em]">Range</div>
                                    </div>
                                    <div className="text-center py-5 px-4">
                                        <div className="text-xl font-AXONALLPrimeRegular text-gray-900 mb-1">{selectedJet.speed}</div>
                                        <div className="text-[10px] text-gray-400 font-poppinsRegular uppercase tracking-[0.2em]">Speed</div>
                                    </div>
                                    <div className="text-center py-5 px-4">
                                        <div className="text-xl font-AXONALLPrimeRegular text-gray-900 mb-1">{selectedJet.category}</div>
                                        <div className="text-[10px] text-gray-400 font-poppinsRegular uppercase tracking-[0.2em]">Category</div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                                        <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-500">Interior Gallery</p>
                                        {selectedJet.interiorSrc.length > 1 && (
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedJetImageIndex((prev) => Math.max(0, prev - 1))}
                                                    disabled={selectedJetImageIndex === 0}
                                                    className="p-1.5 border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                    aria-label="Previous image"
                                                >
                                                    <ChevronDown className="w-4 h-4 transform rotate-90" />
                                                </button>
                                                <span className="text-[11px] text-gray-400 font-poppinsLight">
                                                    {selectedJetImageIndex + 1} / {selectedJet.interiorSrc.length}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setSelectedJetImageIndex((prev) =>
                                                            Math.min(selectedJet.interiorSrc.length - 1, prev + 1)
                                                        )
                                                    }
                                                    disabled={selectedJetImageIndex === selectedJet.interiorSrc.length - 1}
                                                    className="p-1.5 border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                    aria-label="Next image"
                                                >
                                                    <ChevronDown className="w-4 h-4 transform -rotate-90" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="overflow-hidden border border-gray-200">
                                        <img
                                            src={selectedJet.interiorSrc[selectedJetImageIndex]}
                                            alt={`${selectedJet.name} — ${selectedJetImageIndex + 1}`}
                                            className="w-full h-80 object-cover"
                                        />
                                    </div>
                                    {selectedJet.interiorSrc.length > 1 && (
                                        <div className="flex justify-center mt-4 space-x-1.5">
                                            {selectedJet.interiorSrc.map((_, idx) => (
                                                <button
                                                    type="button"
                                                    key={idx}
                                                    onClick={() => setSelectedJetImageIndex(idx)}
                                                    className={`w-2 h-2 transition-colors ${
                                                        idx === selectedJetImageIndex
                                                            ? 'bg-gray-900'
                                                            : 'bg-gray-300 hover:bg-gray-500'
                                                    }`}
                                                    aria-label={`Image ${idx + 1}`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="text-center mt-8">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedJet(null)}
                                        className="px-10 py-3 border border-gray-900 bg-gray-900 text-white text-[11px] tracking-[0.25em] uppercase font-poppinsRegular hover:bg-black transition-colors duration-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* SUCCESS MODAL */}
                {submitSuccess && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                        <div className="bg-white p-10 md:p-12 shadow-2xl max-w-xl w-full text-center relative animate-in fade-in zoom-in duration-300">
                            <button
                                onClick={() => setSubmitSuccess(false)}
                                className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 transition-colors p-2"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="w-16 h-16 border border-gray-200 flex items-center justify-center mx-auto mb-6">
                                <Check className="w-7 h-7 text-gray-900" />
                            </div>
                            <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-3">Confirmation</p>
                            <h2 className="text-2xl font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 mb-4">Request Submitted</h2>
                            <div className="w-8 h-px bg-gray-300 mx-auto mb-6" />
                            <p className="text-gray-500 font-poppinsLight mb-8 text-sm leading-relaxed">Thank you for your private jet inquiry. A member of our team will be in touch with you shortly.</p>
                            <button
                                onClick={() => setSubmitSuccess(false)}
                                className="px-10 py-3 bg-gray-900 text-white text-[11px] tracking-[0.25em] uppercase font-poppinsRegular hover:bg-black transition-colors duration-200"
                            >
                                Close Window
                            </button>
                        </div>
                    </div>
                )}
                {/* INQUIRY DETAILS MODAL */}
                {selectedInquiry && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                        <div className="bg-white shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-300 relative">
                            <div className="sticky top-0 bg-white border-b border-gray-100 px-8 py-5 flex justify-between items-center z-10">
                                <div>
                                    <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-0.5">Agent Portal</p>
                                    <h2 className="text-xl font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900">Inquiry Details</h2>
                                </div>
                                <button
                                    onClick={() => setSelectedInquiry(null)}
                                    className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="p-8 space-y-8">
                                <div>
                                    <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-400 mb-3 pb-2 border-b border-gray-100">Client Information</p>
                                    <div className="grid grid-cols-2 gap-4 text-gray-800 font-poppinsLight">
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Name</span><span className="text-sm">{selectedInquiry.clientName}</span></div>
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Email</span><span className="text-sm">{selectedInquiry.emailAddress}</span></div>
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Phone</span><span className="text-sm">{selectedInquiry.phoneNumber}</span></div>
                                        <div>
                                            <span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Status</span>
                                            <span className="font-poppinsMedium text-sm">{selectedInquiry.status || 'Pending'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-400 mb-3 pb-2 border-b border-gray-100">First Leg</p>
                                    <div className="grid grid-cols-3 gap-4 text-gray-800 font-poppinsLight">
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Date</span><span className="text-sm">{new Date(selectedInquiry.firstLeg?.dateOfDeparture).toLocaleDateString()}</span></div>
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">From</span><span className="text-sm">{selectedInquiry.firstLeg?.departingAirportArea}</span></div>
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">To</span><span className="text-sm">{selectedInquiry.firstLeg?.arrivalAirportArea}</span></div>
                                    </div>
                                </div>

                                {selectedInquiry.returnLeg?.dateOfDeparture && (
                                    <div>
                                        <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-400 mb-3 pb-2 border-b border-gray-100">Return Leg</p>
                                        <div className="grid grid-cols-3 gap-4 text-gray-800 font-poppinsLight">
                                            <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Date</span><span className="text-sm">{new Date(selectedInquiry.returnLeg.dateOfDeparture).toLocaleDateString()}</span></div>
                                            <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">From</span><span className="text-sm">{selectedInquiry.returnLeg.departingAirportArea}</span></div>
                                            <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">To</span><span className="text-sm">{selectedInquiry.returnLeg.arrivalAirportArea}</span></div>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-400 mb-3 pb-2 border-b border-gray-100">Additional Specifications</p>
                                    <div className="grid grid-cols-2 gap-4 text-gray-800 font-poppinsLight">
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Trip Type</span><span className="text-sm">{selectedInquiry.additionalInformation?.tripType}</span></div>
                                        <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Passengers</span><span className="text-sm">{selectedInquiry.additionalInformation?.numberOfPassengers}</span></div>
                                        {selectedInquiry.additionalInformation?.preferredAircraft && <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Aircraft</span><span className="text-sm">{selectedInquiry.additionalInformation.preferredAircraft}</span></div>}
                                        {selectedInquiry.additionalInformation?.budget && <div><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Budget</span><span className="text-sm">{selectedInquiry.additionalInformation.budget}</span></div>}
                                        {selectedInquiry.additionalInformation?.estimatedLuggage && <div className="col-span-2"><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Luggage</span><span className="text-sm">{selectedInquiry.additionalInformation.estimatedLuggage}</span></div>}
                                        {selectedInquiry.additionalInformation?.oversizedLuggage && <div className="col-span-2"><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Oversized</span><span className="text-sm">{selectedInquiry.additionalInformation.oversizedLuggage}</span></div>}
                                        {selectedInquiry.additionalInformation?.specialRequests && <div className="col-span-2"><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Special Requests</span><span className="text-sm">{selectedInquiry.additionalInformation.specialRequests}</span></div>}
                                        {selectedInquiry.additionalInformation?.notes && <div className="col-span-2"><span className="block text-[10px] tracking-[0.15em] uppercase text-gray-400 font-poppinsRegular mb-0.5">Notes</span><span className="text-sm">{selectedInquiry.additionalInformation.notes}</span></div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default PrivateJetPage;
